import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Store, Shield, LogOut, Package } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const SearchBar = ({ className = "" }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <form
      className={`flex items-center rounded-full border border-gray-200 bg-white pl-4 shadow-sm focus-within:ring-2 focus-within:ring-orange-400 ${className}`}
      onSubmit={(e) => { e.preventDefault(); nav(`/recherche?q=${encodeURIComponent(q)}`); }}
    >
      <Search className="h-4 w-4 text-gray-400" />
      <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un produit, une boutique…" className="w-full bg-transparent px-3 py-2.5 text-sm outline-none" />
      <button data-testid="search-submit" className="m-1 rounded-full bg-orange-500 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-orange-600">Chercher</button>
    </form>
  );
};

const AccountMenu = ({ user, logout }) => {
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" data-testid="account-menu-trigger" className="gap-2 rounded-full">
          <User className="h-4 w-4" /> <span className="hidden max-w-[120px] truncate lg:inline">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-account" onClick={() => nav("/compte")}><User className="mr-2 h-4 w-4" />Mon compte</DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/mes-commandes")}><Package className="mr-2 h-4 w-4" />Mes commandes</DropdownMenuItem>
        {user.shop && <DropdownMenuItem data-testid="menu-seller" onClick={() => nav("/vendeur")}><Store className="mr-2 h-4 w-4" />Espace vendeur</DropdownMenuItem>}
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}><Shield className="mr-2 h-4 w-4" />Administration</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }}><LogOut className="mr-2 h-4 w-4" />Déconnexion</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  return (
    <header className="sticky top-0 z-40 border-b border-gray-200/70 bg-white/75 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 md:px-6">
        <Logo />
        <SearchBar className="hidden flex-1 md:flex" />
        <nav className="ml-auto flex items-center gap-1 md:ml-0">
          <Link to="/boutiques" data-testid="nav-shops" className="hidden rounded-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-orange-50 lg:block">Boutiques</Link>
          {!user?.shop && user?.role !== "admin" && (
            <Link to="/devenir-vendeur" data-testid="nav-become-seller" className="hidden rounded-full px-3 py-2 text-sm font-semibold text-green-800 hover:bg-green-50 sm:block">Devenir vendeur</Link>
          )}
          <Link to="/panier" data-testid="nav-cart" className="relative rounded-full p-2.5 hover:bg-orange-50">
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && <span data-testid="cart-count" className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">{count}</span>}
          </Link>
          {user ? (
            <AccountMenu user={user} logout={logout} />
          ) : user === false ? (
            <div className="hidden items-center gap-2 sm:flex">
              <Button asChild variant="ghost" className="rounded-full" data-testid="nav-login"><Link to="/connexion">Connexion</Link></Button>
              <Button asChild className="rounded-full" data-testid="nav-register"><Link to="/inscription">Créer un compte</Link></Button>
            </div>
          ) : null}
        </nav>
      </div>
      <div className="px-4 pb-3 md:hidden"><SearchBar /></div>
    </header>
  );
};
